// src/components/SubmissionChecklist.tsx
// ──────────────────────────────────────────────────────────────
// Pre-submission validation checklist for prior auth cases.
// Displays readiness checks, coding accuracy suggestions,
// and documentation gap analysis.
//
// This component does NOT make clinical decisions. It surfaces
// information to help physicians submit more complete and
// accurately coded authorization requests.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  validateSubmission,
  normalizeInsurer,
  type ValidationCheck,
  type ValidationResult,
  type CaseDataForValidation,
  type CheckSeverity,
  type CheckCategory,
} from "../utils/submissionValidation";

// ── Types ────────────────────────────────────────────────────

interface CodingSuggestion {
  code: string;
  description: string;
  clinical_basis: string;
  question: string;
}

interface CodingAnalysis {
  suggestions: CodingSuggestion[];
  documentation_gaps: string[];
}

interface SubmissionChecklistProps {
  caseData: CaseDataForValidation & { id: string; friendly_name?: string };
  /** Called when the physician adds/edits a field from the checklist.
   *  Parent is responsible for persisting to DB and updating local state. */
  onCaseUpdate?: (fields: Record<string, string>) => void;
}

type AIStatus = "idle" | "loading" | "done" | "error";

// ── Icons ────────────────────────────────────────────────────

function IconCheck({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function IconWarning({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}

function IconError({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function IconInfo({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

function IconChevronDown({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function IconSparkle({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

function IconPlus({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}

function IconPencil({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

// ── Check-to-field mapping ───────────────────────────────────
// Maps check IDs to the case field they can fix, with placeholder text.

interface EditableField {
  field: string;
  placeholder: string;
  multiline?: boolean;
  label: string;
  /** If true, save replaces the entire field instead of appending */
  replaceMode?: boolean;
}

const CHECK_FIELD_MAP: Record<string, EditableField> = {
  missing_insurer: {
    field: "insurance_company",
    placeholder: "e.g., UnitedHealthcare",
    label: "Insurance company",
  },
  missing_diagnosis: {
    field: "diagnoses",
    placeholder: "e.g., M54.41 — Sciatica, right side",
    label: "Diagnosis codes",
  },
  missing_request: {
    field: "request_details",
    placeholder: "e.g., MRI Lumbar Spine without contrast",
    label: "Service requested",
  },
  no_conservative: {
    field: "tried_already",
    placeholder:
      "e.g., 6 weeks physical therapy, NSAIDs, activity modification",
    multiline: true,
    label: "Conservative treatments",
  },
  no_duration: {
    field: "tried_already",
    placeholder: "e.g., 6 weeks physical therapy starting 01/10/2026",
    multiline: true,
    label: "Treatment details with duration",
  },
  no_clinical_notes: {
    field: "additional_context",
    placeholder:
      "e.g., Radiating pain L leg, positive SLR, decreased sensation L5 dermatome",
    multiline: true,
    label: "Clinical notes",
  },
  // Passed / coding checks — these allow changing an existing value
  has_diagnosis: {
    field: "diagnoses",
    placeholder: "e.g., M54.41 — Sciatica, right side",
    label: "Diagnosis codes",
    replaceMode: true,
  },
};

/** Get field definition for a check ID, including wildcard matches for unspecified_ codes */
function getFieldDef(checkId: string): EditableField | undefined {
  if (CHECK_FIELD_MAP[checkId]) return CHECK_FIELD_MAP[checkId];
  if (checkId.startsWith("unspecified_")) {
    return {
      field: "diagnoses",
      placeholder: "e.g., M54.41 — Sciatica, right side",
      label: "Diagnosis codes",
      replaceMode: true,
    };
  }
  return undefined;
}

// ── Severity Styles ──────────────────────────────────────────

function getSeverityIcon(severity: CheckSeverity) {
  switch (severity) {
    case "pass":
      return <IconCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
    case "warning":
      return <IconWarning className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    case "error":
      return <IconError className="w-4 h-4 text-red-500 flex-shrink-0" />;
    case "info":
      return <IconInfo className="w-4 h-4 text-blue-400 flex-shrink-0" />;
  }
}

function getSeverityBg(severity: CheckSeverity): string {
  switch (severity) {
    case "pass":
      return "bg-emerald-50 border-emerald-100";
    case "warning":
      return "bg-amber-50 border-amber-100";
    case "error":
      return "bg-red-50 border-red-100";
    case "info":
      return "bg-blue-50 border-blue-100";
  }
}

// ── Score Ring ───────────────────────────────────────────────

function ScoreRing({ score }: { score: number }) {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const color =
    score >= 80
      ? "text-emerald-500"
      : score >= 50
      ? "text-amber-500"
      : "text-red-500";

  return (
    <div className="relative w-12 h-12 flex-shrink-0">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="3"
        />
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          className={color}
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <span
        className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${color}`}
      >
        {score}
      </span>
    </div>
  );
}

// ── Category Labels ──────────────────────────────────────────

const CATEGORY_LABELS: Record<CheckCategory, string> = {
  completeness: "Required Fields",
  documentation: "Clinical Documentation",
  insurer: "Insurer Requirements",
  coding: "Coding Accuracy",
};

// ── Component ────────────────────────────────────────────────

export function SubmissionChecklist({
  caseData,
  onCaseUpdate,
}: SubmissionChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPasses, setShowPasses] = useState(false);
  const [aiStatus, setAIStatus] = useState<AIStatus>("idle");
  const [codingAnalysis, setCodingAnalysis] = useState<CodingAnalysis | null>(
    null
  );

  // Inline edit state
  const [editingCheckId, setEditingCheckId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiExpanded, setAIExpanded] = useState(false);
  const [editingGapIdx, setEditingGapIdx] = useState<number | null>(null);
  const [gapDraft, setGapDraft] = useState("");

  const normalizedInsurer = useMemo(
    () => normalizeInsurer(caseData.insurance_company),
    [caseData.insurance_company]
  );

  // Run client-side validation
  const validation: ValidationResult = useMemo(
    () => validateSubmission(caseData, normalizedInsurer),
    [caseData, normalizedInsurer]
  );

  // Handle inline save
  const handleSave = useCallback(
    (checkId: string) => {
      const fieldDef = getFieldDef(checkId);
      if (!fieldDef || !editDraft.trim() || !onCaseUpdate) return;

      setSaving(true);

      let newValue: string;
      if (fieldDef.replaceMode) {
        // Replace mode — overwrite the entire field
        newValue = editDraft.trim();
      } else {
        // Append mode — add to existing content
        const existingValue = (caseData as Record<string, unknown>)[
          fieldDef.field
        ] as string | undefined;
        newValue = existingValue?.trim()
          ? `${existingValue}; ${editDraft.trim()}`
          : editDraft.trim();
      }

      onCaseUpdate({ [fieldDef.field]: newValue });

      setSaving(false);
      setEditingCheckId(null);
      setEditDraft("");
    },
    [editDraft, onCaseUpdate, caseData]
  );

  // Handle accepting an AI coding suggestion
  const handleAcceptCode = useCallback(
    (code: string, description: string) => {
      if (!onCaseUpdate) return;

      const existing = caseData.diagnoses?.trim();
      const entry = `${code} — ${description}`;
      const newValue = existing ? `${existing}; ${entry}` : entry;

      onCaseUpdate({ diagnoses: newValue });
    },
    [onCaseUpdate, caseData.diagnoses]
  );

  // Handle adding documentation from a gap suggestion
  const handleSaveGap = useCallback(() => {
    if (!gapDraft.trim() || !onCaseUpdate) return;

    const existing = caseData.additional_context?.trim();
    const newValue = existing
      ? `${existing}\n${gapDraft.trim()}`
      : gapDraft.trim();

    onCaseUpdate({ additional_context: newValue });
    setEditingGapIdx(null);
    setGapDraft("");
  }, [gapDraft, onCaseUpdate, caseData.additional_context]);

  // AI coding analysis
  const runCodingAnalysis = useCallback(async () => {
    const hasContext =
      caseData.additional_context?.trim() || caseData.tried_already?.trim();
    if (!hasContext) return; // Need clinical notes for AI analysis

    setAIStatus("loading");
    try {
      const resp = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/analyze-coding-accuracy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            current_codes: caseData.diagnoses,
            request_details: caseData.request_details,
            conservative_treatments: caseData.tried_already,
            clinical_notes: caseData.additional_context,
          }),
        }
      );

      if (!resp.ok) throw new Error("Analysis failed");

      const data: CodingAnalysis = await resp.json();
      setCodingAnalysis(data);
      setAIStatus("done");
    } catch (e) {
      console.error("[SubmissionChecklist] AI analysis error:", e);
      setAIStatus("error");
    }
  }, [caseData]);

  // Auto-run AI analysis when expanded and context available
  useEffect(() => {
    if (isExpanded && aiStatus === "idle") {
      const hasContext =
        caseData.additional_context?.trim() || caseData.tried_already?.trim();
      if (hasContext) {
        runCodingAnalysis();
      }
    }
  }, [isExpanded, aiStatus, runCodingAnalysis, caseData]);

  // Group checks by category
  const groupedChecks = useMemo(() => {
    const groups: Record<CheckCategory, ValidationCheck[]> = {
      completeness: [],
      documentation: [],
      insurer: [],
      coding: [],
    };
    for (const check of validation.checks) {
      groups[check.category].push(check);
    }
    return groups;
  }, [validation]);

  const actionableCount = validation.checks.filter(
    (c) => c.severity === "error" || c.severity === "warning"
  ).length;

  const hasAISuggestions =
    codingAnalysis &&
    (codingAnalysis.suggestions.length > 0 ||
      codingAnalysis.documentation_gaps.length > 0);

  // Don't render if case is already submitted/approved
  if (
    caseData.case_status &&
    ["submitted", "approved", "appealed"].includes(caseData.case_status)
  ) {
    return null;
  }

  return (
    <div>
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center gap-3 px-5 py-[18px] text-left group transition-colors duration-150 hover:bg-slate-50 ${
          isExpanded ? "border-b border-slate-100" : ""
        }`}
      >
        <IconCheck className="w-3.5 h-3.5 text-slate-400 opacity-90 flex-shrink-0" />

        <span className="text-sm font-medium text-slate-700 flex-1 min-w-0">
          Submission Readiness
        </span>

        {actionableCount > 0 ? (
          <span className="text-[11px] font-medium text-amber-500">
            {actionableCount} item{actionableCount !== 1 ? "s" : ""} to review
          </span>
        ) : (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
            <span className="text-[10px] font-medium text-teal-600">Ready</span>
          </>
        )}

        <IconChevronDown
          className={`w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-5 pt-4 pb-4 space-y-4">
          {/* Category groups */}
          {(Object.keys(groupedChecks) as CheckCategory[]).map((category) => {
            const checks = groupedChecks[category];
            if (checks.length === 0) return null;

            const actionable = checks.filter((c) => c.severity !== "pass");
            const passed = checks.filter((c) => c.severity === "pass");
            const displayChecks = showPasses ? checks : actionable;

            if (displayChecks.length === 0 && !showPasses) return null;

            return (
              <div key={category} className="pt-3">
                <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  {CATEGORY_LABELS[category]}
                  {passed.length > 0 && !showPasses && (
                    <span className="ml-1.5 text-emerald-500 normal-case tracking-normal font-normal">
                      ({passed.length} passed)
                    </span>
                  )}
                </h4>
                <div className="space-y-2">
                  {displayChecks.map((check) => {
                    const fieldDef = getFieldDef(check.id);
                    const isReplace = !!fieldDef?.replaceMode;
                    const canEdit =
                      fieldDef &&
                      onCaseUpdate &&
                      (check.severity !== "pass" || isReplace);
                    const isEditing = editingCheckId === check.id;

                    return (
                      <div
                        key={check.id}
                        className={`px-3 py-2.5 rounded-lg border ${getSeverityBg(
                          check.severity
                        )}`}
                      >
                        <div className="flex gap-2.5">
                          <div className="mt-0.5">
                            {getSeverityIcon(check.severity)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-medium text-slate-800">
                                  {check.title}
                                </p>
                                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                                  {check.detail}
                                </p>
                              </div>
                              {canEdit && !isEditing && (
                                <button
                                  onClick={() => {
                                    setEditingCheckId(check.id);
                                    // Pre-fill with existing value for replace mode
                                    const currentVal = isReplace
                                      ? ((caseData as Record<string, unknown>)[
                                          fieldDef.field
                                        ] as string) || ""
                                      : "";
                                    setEditDraft(currentVal);
                                  }}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors flex-shrink-0 ${
                                    isReplace
                                      ? "text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200"
                                      : "text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200"
                                  }`}
                                >
                                  {isReplace ? (
                                    <>
                                      <IconPencil className="w-3 h-3" />
                                      Change
                                    </>
                                  ) : (
                                    <>
                                      <IconPlus className="w-3 h-3" />
                                      Add
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                            {check.disclaimer && (
                              <p className="text-[10px] text-slate-400 mt-1.5 italic">
                                {check.disclaimer}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Inline editor */}
                        {isEditing && fieldDef && (
                          <div className="mt-2.5 ml-6.5 pl-[26px]">
                            <label className="text-[11px] font-medium text-slate-600 mb-1 block">
                              {fieldDef.label}
                            </label>
                            {fieldDef.multiline ? (
                              <textarea
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                placeholder={fieldDef.placeholder}
                                rows={2}
                                autoFocus
                                className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                              />
                            ) : (
                              <input
                                type="text"
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                placeholder={fieldDef.placeholder}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && editDraft.trim())
                                    handleSave(check.id);
                                  if (e.key === "Escape") {
                                    setEditingCheckId(null);
                                    setEditDraft("");
                                  }
                                }}
                                className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                              />
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                              <button
                                onClick={() => handleSave(check.id)}
                                disabled={!editDraft.trim() || saving}
                                className="px-2.5 py-1 rounded-md text-[11px] font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                {saving ? "Saving..." : "Save"}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCheckId(null);
                                  setEditDraft("");
                                }}
                                className="px-2.5 py-1 rounded-md text-[11px] font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* AI Coding Analysis Section */}
          {(aiStatus !== "idle" || hasAISuggestions) && (
            <div className="pt-3">
              {/* Collapsible header */}
              <button
                onClick={() => setAIExpanded(!aiExpanded)}
                className="w-full flex items-center gap-1.5 text-left group"
              >
                <IconSparkle className="w-3.5 h-3.5 text-violet-400" />
                <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  AI Coding Review
                </h4>
                {aiStatus === "done" && codingAnalysis && (
                  <span className="text-[10px] text-violet-500 font-normal normal-case tracking-normal">
                    (
                    {codingAnalysis.suggestions.length +
                      codingAnalysis.documentation_gaps.length}{" "}
                    item
                    {codingAnalysis.suggestions.length +
                      codingAnalysis.documentation_gaps.length !==
                    1
                      ? "s"
                      : ""}
                    )
                  </span>
                )}
                {aiStatus === "loading" && (
                  <div className="w-3 h-3 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin flex-shrink-0 ml-1" />
                )}
                <IconChevronDown
                  className={`w-3.5 h-3.5 text-slate-400 ml-auto transition-transform duration-200 ${
                    aiExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Collapsed summary */}
              {!aiExpanded && aiStatus === "done" && codingAnalysis && (
                <p className="text-[11px] text-slate-400 mt-1 ml-5">
                  {codingAnalysis.suggestions.length > 0
                    ? `${codingAnalysis.suggestions.length} coding suggestion${
                        codingAnalysis.suggestions.length !== 1 ? "s" : ""
                      }`
                    : "No coding suggestions"}
                  {codingAnalysis.documentation_gaps.length > 0 &&
                    ` · ${
                      codingAnalysis.documentation_gaps.length
                    } documentation gap${
                      codingAnalysis.documentation_gaps.length !== 1 ? "s" : ""
                    }`}
                </p>
              )}

              {/* Expanded content */}
              {aiExpanded && (
                <div className="mt-2 space-y-2">
                  {aiStatus === "loading" && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-violet-50 border border-violet-100">
                      <div className="w-4 h-4 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin flex-shrink-0" />
                      <p className="text-xs text-violet-700">
                        Analyzing clinical notes for coding accuracy...
                      </p>
                    </div>
                  )}

                  {aiStatus === "error" && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200">
                      <p className="text-xs text-slate-500">
                        Coding analysis unavailable — structural checks still
                        apply
                      </p>
                    </div>
                  )}

                  {aiStatus === "done" && codingAnalysis && (
                    <>
                      {/* Coding suggestions */}
                      {codingAnalysis.suggestions.map((suggestion, i) => (
                        <div
                          key={i}
                          className="px-3 py-3 rounded-lg bg-violet-50 border border-violet-100"
                        >
                          <p className="text-sm font-medium text-violet-900">
                            {suggestion.question}
                          </p>
                          <p className="text-xs text-violet-700 mt-1 leading-relaxed">
                            {suggestion.clinical_basis}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-violet-100 text-violet-800">
                              {suggestion.code}
                            </span>
                            <span className="text-[11px] text-violet-600">
                              {suggestion.description}
                            </span>
                          </div>
                          {onCaseUpdate && (
                            <button
                              onClick={() =>
                                handleAcceptCode(
                                  suggestion.code,
                                  suggestion.description
                                )
                              }
                              className="mt-2 flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:text-violet-800 underline underline-offset-2"
                            >
                              <IconPlus className="w-3 h-3" />
                              Add to diagnosis codes
                            </button>
                          )}
                        </div>
                      ))}

                      {/* Documentation gaps — with Add buttons */}
                      {codingAnalysis.documentation_gaps.length > 0 && (
                        <div className="px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100">
                          <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wider mb-1.5">
                            Documentation Gaps
                          </p>
                          {codingAnalysis.documentation_gaps.map((gap, i) => (
                            <div key={i} className="mt-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-xs text-blue-700 leading-relaxed flex gap-1.5">
                                  <span className="text-blue-400 flex-shrink-0">
                                    •
                                  </span>
                                  {gap}
                                </p>
                                {onCaseUpdate && editingGapIdx !== i && (
                                  <button
                                    onClick={() => {
                                      setEditingGapIdx(i);
                                      setGapDraft("");
                                    }}
                                    className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors flex-shrink-0"
                                  >
                                    <IconPlus className="w-2.5 h-2.5" />
                                    Add
                                  </button>
                                )}
                              </div>

                              {/* Inline gap editor */}
                              {editingGapIdx === i && (
                                <div className="mt-1.5 ml-4">
                                  <input
                                    type="text"
                                    value={gapDraft}
                                    onChange={(e) =>
                                      setGapDraft(e.target.value)
                                    }
                                    placeholder={`e.g., ${
                                      gap.toLowerCase().includes("laterality")
                                        ? "Right-sided radiculopathy"
                                        : gap.toLowerCase().includes("duration")
                                        ? "Symptoms onset 8 weeks ago"
                                        : "Enter clinical detail..."
                                    }`}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && gapDraft.trim())
                                        handleSaveGap();
                                      if (e.key === "Escape") {
                                        setEditingGapIdx(null);
                                        setGapDraft("");
                                      }
                                    }}
                                    className="w-full px-2 py-1 rounded border border-blue-200 bg-white text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                  />
                                  <div className="flex items-center gap-2 mt-1">
                                    <button
                                      onClick={() => handleSaveGap()}
                                      disabled={!gapDraft.trim()}
                                      className="px-2 py-0.5 rounded text-[10px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                      Save to clinical notes
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingGapIdx(null);
                                        setGapDraft("");
                                      }}
                                      className="px-2 py-0.5 rounded text-[10px] font-medium text-slate-500 hover:bg-slate-100 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {codingAnalysis.suggestions.length === 0 &&
                        codingAnalysis.documentation_gaps.length === 0 && (
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                            <IconCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <p className="text-xs text-emerald-700">
                              Current coding appears consistent with clinical
                              documentation
                            </p>
                          </div>
                        )}

                      {/* Disclaimer */}
                      <p className="text-[10px] text-slate-400 italic text-center mt-2 leading-relaxed">
                        AI coding review is based on your clinical
                        documentation. All coding decisions are the
                        responsibility of the treating physician.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Toggle passed checks */}
          {validation.checks.some((c) => c.severity === "pass") && (
            <button
              onClick={() => setShowPasses(!showPasses)}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPasses ? "Hide" : "Show"} passed checks
            </button>
          )}
        </div>
      )}
    </div>
  );
}
